import {useEffect, useRef, useState} from 'react';
import axios from 'axios';
import {v4 as uuid} from 'uuid';
import ajax, {ajaxConfig} from '../ajax/ajax';
import {uploadFile} from '../upload/chunkedUploads';

// Mirrors GALLERY_VIDEO_MAX_DURATION in the API. Checking here as well is only there to
// spare the user a gigabyte-sized upload that the server would reject anyway -- the server
// stays the authority and re-checks with ffprobe.
export const maxVideoDurationSeconds = 300;

const statusPollIntervalMs = 4000;

function isVideo(file) {
  return file.type.startsWith('video/');
}

// The browser can tell us how long the clip is before a single byte goes over the wire.
function readVideoDuration(file) {
  return new Promise((resolve) => {
    const element = document.createElement('video');
    const objectUrl = URL.createObjectURL(file);

    const done = (duration) => {
      URL.revokeObjectURL(objectUrl);
      resolve(duration);
    };

    element.preload = 'metadata';
    element.onloadedmetadata = () => {
      done(isNaN(element.duration) ? null : element.duration);
    };
    // A container the browser cannot parse is not automatically one ffmpeg cannot parse,
    // so an unreadable duration means "let the server decide", not "reject".
    element.onerror = () => {
      done(null);
    };
    element.src = objectUrl;
  });
}

function itemFromPayload(payload, localId, overrides = {}) {
  return {
    id: localId,
    databaseId: payload.id,
    mediaType: payload.mediaType,
    status: payload.status,
    statusMessage: payload.statusMessage,
    duration: payload.duration,
    fileName: payload.fileName,
    thumbnail: payload.small,
    text: payload.text ?? '',
    isPreview: false,
    ...overrides,
  };
}

// Shared by the create and the edit screen so a clip behaves identically in both.
const useGalleryUploader = ({items, setItems}) => {
  const [uploads, setUploads] = useState({});

  // The queue has to survive re-renders; both screens previously rebuilt it on every
  // render, which meant two files chosen at once could upload concurrently.
  const queueRef = useRef({running: false, entries: []});

  function handleQueue() {
    const queue = queueRef.current;
    if (queue.running || queue.entries.length === 0) {
      return;
    }
    queue.running = true;
    const handlerFunction = queue.entries.shift();
    handlerFunction().catch(() => {}).then(() => {
      queue.running = false;
      handleQueue();
    });
  }

  function setUploadError(localId, message) {
    setUploads((old) => {
      if (!old[localId]) {
        return old;
      }

      return {...old, [localId]: {...old[localId], error: message}};
    });
  }

  function setUploadProgress(localId, progress) {
    setUploads((old) => {
      if (!old[localId]) {
        return old;
      }

      return {...old, [localId]: {...old[localId], progress}};
    });
  }

  function clearUpload(localId) {
    setUploads((old) => {
      const newState = {...old};
      delete newState[localId];

      return newState;
    });
  }

  function uploadPhoto(file, localId) {
    return axios.post(
      `${ajaxConfig.baseUrl}/api/v1/upload/photo`,
      file,
      {
        params: {fileName: file.name},
        headers: {"Content-Type": "application/octet-stream", "x-auth-token": ajaxConfig.token},
        onUploadProgress: (progressEvent) => {
          setUploadProgress(localId, progressEvent.progress * 100);
        },
      }
    ).then((response) => {
      if (!response.data.success) {
        throw new Error('Unbekannter Fehler');
      }
      setItems((oldState) => [...oldState, itemFromPayload({
        id: response.data.id,
        mediaType: 'image',
        status: 'ready',
        statusMessage: null,
        duration: null,
        fileName: file.name,
        small: response.data.thumbnail,
        text: '',
      }, localId)]);
      clearUpload(localId);
    });
  }

  async function uploadVideo(file, localId) {
    const duration = await readVideoDuration(file);
    if (duration !== null && duration > maxVideoDurationSeconds) {
      throw new Error(
        `Das Video ist ${Math.round(duration)} Sekunden lang. In einer Galerie sind höchstens `
        + `${Math.floor(maxVideoDurationSeconds / 60)} Minuten erlaubt, längere Videos gehören in den Video-Bereich.`
      );
    }

    let finishedItem = null;
    await uploadFile(file, 3, (progress, done, id, data) => {
      setUploadProgress(localId, progress);
      if (done && data && data.item) {
        finishedItem = data.item;
      }
    }, null, '/api/v1/upload/gallery-video/finish');

    if (!finishedItem) {
      throw new Error('Video konnte nicht verarbeitet werden.');
    }

    setItems((oldState) => [...oldState, itemFromPayload(finishedItem, localId)]);
    clearUpload(localId);
  }

  function pushToQueue(file, localId) {
    queueRef.current.entries.push(() => {
      const upload = isVideo(file) ? uploadVideo(file, localId) : uploadPhoto(file, localId);

      return upload.catch((err) => {
        const message = err.response?.data?.message ?? err.message ?? 'Unbekannter Fehler';
        setUploadError(localId, message);
        throw err;
      });
    });
    handleQueue();
  }

  function handleFileEvent(e) {
    const chosenFiles = Array.prototype.slice.call(e.target.files);
    chosenFiles.forEach((file) => {
      const localId = uuid();
      setUploads((oldState) => ({
        ...oldState,
        [localId]: {file, id: localId, progress: 0, name: file.name, error: null, isVideo: isVideo(file)},
      }));
      pushToQueue(file, localId);
    });
    // Otherwise picking the same file again after removing it fires no change event.
    e.target.value = '';
  }

  function removeUploadingItem(localId) {
    clearUpload(localId);
  }

  function retry(localId) {
    const upload = uploads[localId];
    if (!upload) {
      return;
    }
    setUploads((old) => ({...old, [localId]: {...old[localId], progress: 0, error: null}}));
    pushToQueue(upload.file, localId);
  }

  // Clips keep encoding after their upload finished, so the tiles poll until they settle.
  const pendingIds = items
    .filter((item) => item.status === 'pending' || item.status === 'processing')
    .map((item) => item.databaseId);
  const pendingKey = pendingIds.join(',');

  useEffect(() => {
    if (pendingKey === '') {
      return undefined;
    }

    let request = null;
    const timer = setInterval(() => {
      request = ajax.get(`/api/v1/upload/gallery-media/status?ids=${pendingKey}`, (data) => {
        setItems((oldState) => oldState.map((item) => {
          const fresh = data.items.find((candidate) => candidate.id === item.databaseId);
          if (!fresh || fresh.status === item.status) {
            return item;
          }

          return {
            ...item,
            status: fresh.status,
            statusMessage: fresh.statusMessage,
            duration: fresh.duration,
            thumbnail: fresh.small,
          };
        }));
      });
    }, statusPollIntervalMs);

    return () => {
      clearInterval(timer);
      if (request) {
        request.cancel();
      }
    };
  }, [pendingKey, setItems]);

  return {
    uploads,
    handleFileEvent,
    removeUploadingItem,
    retry,
    hasPendingMedia: pendingIds.length > 0,
  };
};

export default useGalleryUploader;
